import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Card';
import { BookingSteps } from './BookingForm';
import { api } from '../../lib/api';
import { launchRazorpayCheckout } from '../../lib/razorpay';
import type { Order } from '@bharat-consultancy/shared';

export default function OrderSummary() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    api.orders
      .get(orderId)
      .then((res) => setOrder(res.order))
      .catch(() => setError('Could not load order details.'))
      .finally(() => setIsLoading(false));
  }, [orderId]);

  async function handleProceedToPay() {
    if (!order) return;
    setIsPaying(true);
    setError(null);
    try {
      const checkoutParams = await api.orders.pay(order.id);
      await launchRazorpayCheckout({
        keyId: checkoutParams.razorpayKeyId,
        amount: checkoutParams.amount,
        currency: checkoutParams.currency,
        razorpayOrderId: checkoutParams.razorpayOrderId,
        customerName: order.contactName,
        customerEmail: order.contactEmail,
        customerPhone: order.contactMobile,
        onSuccess: () => {
          // Fulfillment (status update, invoice, email) happens via the
          // server-verified webhook, not this client callback — see
          // paymentService.ts. This redirect is just the UX path; the
          // Thank You page polls/displays based on the order's current
          // status once the webhook has landed.
          navigate(`/booking/${order.service?.slug ?? 'service'}/thank-you?orderId=${order.id}`);
        },
        onDismiss: () => setIsPaying(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start payment. Please try again.');
      setIsPaying(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Order Not Found</h1>
      </div>
    );
  }

  return (
    <>
      <SEOHead title="Order Summary — Bharat Consultancy" description="" canonicalPath="/booking/summary" />

      <section className="bg-paper py-10">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <BookingSteps current={3} />

          <div className="mt-8 rounded-lg border border-line bg-white p-6">
            <h1 className="font-display text-2xl font-bold text-ink">Order Summary</h1>
            <p className="mt-1 font-mono text-sm text-ink/50">Order No: {order.orderNumber}</p>

            <div className="stub-divider mt-6 space-y-3 pt-6">
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Service</span>
                <span className="font-medium text-ink">{order.service?.title ?? 'Service'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Subtotal</span>
                <span className="font-mono text-ink">₹{order.baseAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Tax (GST)</span>
                <span className="font-mono text-ink">₹{order.taxAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="stub-divider flex justify-between pt-3 text-base font-semibold">
                <span className="text-ink">Total</span>
                <span className="font-mono text-seal-blue">₹{order.totalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="mt-6 rounded bg-paper p-4 text-sm text-ink/70">
              <p>
                <span className="font-medium text-ink">Contact:</span> {order.contactName} · {order.contactMobile}
              </p>
              {order.callbackDate && (
                <p className="mt-1">
                  <span className="font-medium text-ink">Callback:</span> {order.callbackDate} {order.callbackTime}
                </p>
              )}
            </div>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

            <Button onClick={handleProceedToPay} className="mt-6 w-full" disabled={isPaying}>
              {isPaying ? 'Opening Payment...' : `Proceed To Pay ₹${order.totalAmount.toLocaleString('en-IN')}`}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
